import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Trash2, Tag, Unlink, X, Loader2, Sparkles, Download, FolderInput, ShieldAlert, Tags } from 'lucide-react';

interface BulkActionsBarProps {
  count: number;
  onClear: () => void;
  /** Delete action — required for execution context, optional for test-case context. */
  onDelete?: () => Promise<void>;
  /** True when at least one selected execution already belongs to a group. */
  hasGroupedSelected?: boolean;
  /** Assign selected executions to a group. */
  onGroup?: (groupName: string) => Promise<void>;
  /** Removes the groupName from all selected executions. */
  onUngroup?: () => Promise<void>;
  /** AI optimizer (test-case context only). Shown when showOptimize is true. */
  onOptimize?: () => Promise<void>;
  /** Controls visibility of the "Optimize with AI" button. */
  showOptimize?: boolean;
  /** Export to CSV action. Optional. */
  onExportCSV?: () => void;
  /** Move to folder (test-case context). Called with the chosen folder _id. */
  onMoveToFolder?: (targetFolderId: string) => Promise<void>;
  /** Available folders for the move-to-folder picker. */
  folders?: Array<{ _id: string; name: string; path: string }>;
  /** Edit severity in bulk (test-case context). Called with the chosen severity string. */
  onEditSeverity?: (severity: string) => Promise<void>;
  /** Edit tags in bulk (test-case context). Called with the final tags array (additive). */
  onEditTags?: (tags: string[]) => Promise<void>;
}

export function BulkActionsBar({
  count,
  hasGroupedSelected = false,
  onDelete,
  onGroup,
  onUngroup,
  onClear,
  onOptimize,
  showOptimize = false,
  onExportCSV,
  onMoveToFolder,
  folders = [],
  onEditSeverity,
  onEditTags,
}: BulkActionsBarProps) {
  const [groupPopoverOpen,    setGroupPopoverOpen]    = useState(false);
  const [folderPopoverOpen,   setFolderPopoverOpen]   = useState(false);
  const [severityPopoverOpen, setSeverityPopoverOpen] = useState(false);
  const [tagsPopoverOpen,     setTagsPopoverOpen]     = useState(false);
  const [selectedFolderId,    setSelectedFolderId]    = useState('');
  const [groupName,   setGroupName]   = useState('');
  const [tagInput,    setTagInput]    = useState('');
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [loading, setLoading] = useState<'delete' | 'group' | 'ungroup' | 'optimize' | 'move' | 'severity' | 'tags' | null>(null);

  // Slide-up animation: start hidden, reveal after first paint so the CSS
  // transition has a baseline to animate from.
  const [isVisible, setIsVisible] = useState(false);
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Popover panel refs (used for outside-click detection)
  const popoverRef         = useRef<HTMLDivElement>(null);
  const folderPopoverRef   = useRef<HTMLDivElement>(null);
  const severityPopoverRef = useRef<HTMLDivElement>(null);
  const tagsPopoverRef     = useRef<HTMLDivElement>(null);

  // Trigger button refs (excluded from outside-click so toggle works correctly)
  const folderBtnRef   = useRef<HTMLButtonElement>(null);
  const severityBtnRef = useRef<HTMLButtonElement>(null);
  const tagsBtnRef     = useRef<HTMLButtonElement>(null);
  const groupBtnRef    = useRef<HTMLButtonElement>(null);

  const inputRef    = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Outside-click helpers — exclude the trigger button so toggle still works
  useEffect(() => {
    if (!folderPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        folderPopoverRef.current && !folderPopoverRef.current.contains(t) &&
        folderBtnRef.current    && !folderBtnRef.current.contains(t)
      ) setFolderPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [folderPopoverOpen]);

  useEffect(() => {
    if (!severityPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        severityPopoverRef.current && !severityPopoverRef.current.contains(t) &&
        severityBtnRef.current     && !severityBtnRef.current.contains(t)
      ) setSeverityPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [severityPopoverOpen]);

  useEffect(() => {
    if (!tagsPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        tagsPopoverRef.current && !tagsPopoverRef.current.contains(t) &&
        tagsBtnRef.current     && !tagsBtnRef.current.contains(t)
      ) setTagsPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tagsPopoverOpen]);

  useEffect(() => {
    if (!groupPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current  && !popoverRef.current.contains(t) &&
        groupBtnRef.current && !groupBtnRef.current.contains(t)
      ) setGroupPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [groupPopoverOpen]);

  const handleMoveToFolder = async () => {
    if (!selectedFolderId || !onMoveToFolder) return;
    setLoading('move');
    try { await onMoveToFolder(selectedFolderId); setFolderPopoverOpen(false); }
    catch { /* upstream handles errors */ }
    finally { setLoading(null); }
  };

  const handleEditSeverity = async (sev: string) => {
    if (!onEditSeverity) return;
    setLoading('severity');
    try { await onEditSeverity(sev); setSeverityPopoverOpen(false); }
    catch { /* upstream handles errors */ }
    finally { setLoading(null); }
  };

  function addPendingTag(raw: string) {
    const t = raw.trim().toLowerCase().replace(/,/g, '');
    if (!t || pendingTags.includes(t)) return;
    setPendingTags((prev) => [...prev, t]);
    setTagInput('');
    tagInputRef.current?.focus();
  }

  const handleApplyTags = async () => {
    if (!onEditTags || pendingTags.length === 0) return;
    setLoading('tags');
    try { await onEditTags(pendingTags); setTagsPopoverOpen(false); setPendingTags([]); setTagInput(''); }
    catch { /* upstream handles errors */ }
    finally { setLoading(null); }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setLoading('delete');
    try { await onDelete(); }
    catch { /* Error is alerted upstream; keep bar visible so user can retry */ }
    finally { setLoading(null); }
  };

  const handleOptimize = async () => {
    if (!onOptimize) return;
    setLoading('optimize');
    try { await onOptimize(); }
    catch { /* Error is alerted upstream */ }
    finally { setLoading(null); }
  };

  const handleUngroup = async () => {
    setLoading('ungroup');
    try { await onUngroup(); }
    catch { /* Error is alerted upstream */ }
    finally { setLoading(null); }
  };

  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = groupName.trim();
    if (!trimmed) return;
    setLoading('group');
    try {
      await onGroup(trimmed);
      setGroupName('');
      setGroupPopoverOpen(false);
    } catch {
      // Error is alerted upstream; keep popover open for retry
    } finally {
      setLoading(null);
    }
  };

  // Shared button classes — single-line, fixed height, no shrink
  const btnBase = 'flex items-center gap-1.5 px-3 h-10 rounded-lg text-white text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0';

  return (
    // Outer: fixed position + slide-up animation only.
    // Must NOT have `relative`, `transform`, `filter`, or `perspective` —
    // any of those would turn `fixed` into `absolute`.
    // `flex justify-center` centres the inner pill on desktop without needing
    // `left-1/2 -translate-x-1/2` (which would conflict with translate-y animation).
    <div
      className={`fixed bottom-0 left-0 right-0 z-[100] flex justify-center
        transition-transform duration-300 ease-out
        ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}
    >
      {/* Inner: visual container + `relative` context for `absolute bottom-full` popovers.
          Kept separate so its `relative` cannot affect the parent's `fixed` stacking. */}
      <div
        data-testid="bulk-actions-bar"
        role="toolbar"
        aria-label="Bulk actions"
        className="relative w-full md:w-auto md:max-w-[calc(100vw-2rem)] md:mb-4
          rounded-t-2xl md:rounded-2xl bg-slate-900 text-white shadow-2xl border border-slate-700"
      >
      {/* ── Scrollable button row ───────────────────────────────────────────────
          Popovers are rendered OUTSIDE this div (below) so overflow-x:auto
          cannot clip them on mobile.                                          */}
      {/* pb-4: leaves breathing room above the iOS/Android home-swipe bar on mobile */}
      <div className="flex items-center gap-2 px-3 md:px-4 py-2.5 md:py-3 pb-4 md:pb-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:overflow-x-visible md:flex-wrap md:justify-center">

        {/* Selection count badge */}
        <span className="flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-blue-500 text-xs font-bold shrink-0">
          {count}
        </span>
        <span className="text-sm font-medium text-slate-300 shrink-0 whitespace-nowrap mr-1">selected</span>

        <div className="w-px h-6 bg-slate-600 mx-1 shrink-0" />

        {/* AI Optimize */}
        {showOptimize && onOptimize && (
          <button
            data-testid="bulk-optimize-button"
            onClick={handleOptimize}
            disabled={!!loading}
            className={`${btnBase} bg-violet-600 hover:bg-violet-500`}
          >
            {loading === 'optimize' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <Sparkles size={16} className="shrink-0" />}
            Optimize with AI
          </button>
        )}

        {/* Move to Folder — trigger only; popover rendered below */}
        {onMoveToFolder && (
          <button
            ref={folderBtnRef}
            data-testid="bulk-move-folder-button"
            aria-expanded={folderPopoverOpen}
            onClick={() => { setSelectedFolderId(folders[0]?._id ?? ''); setFolderPopoverOpen(v => !v); }}
            disabled={!!loading}
            className={`${btnBase} bg-slate-600 hover:bg-slate-500`}
          >
            {loading === 'move' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <FolderInput size={16} className="shrink-0" />}
            Move to Folder
          </button>
        )}

        {/* Edit Tags — trigger only; popover rendered below */}
        {onEditTags && (
          <button
            ref={tagsBtnRef}
            data-testid="bulk-edit-tags-button"
            aria-expanded={tagsPopoverOpen}
            onClick={() => { setPendingTags([]); setTagInput(''); setTagsPopoverOpen(v => !v); }}
            disabled={!!loading}
            className={`${btnBase} bg-slate-600 hover:bg-slate-500`}
          >
            {loading === 'tags' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <Tags size={16} className="shrink-0" />}
            Edit Tags
          </button>
        )}

        {/* Set Severity — trigger only; popover rendered below */}
        {onEditSeverity && (
          <button
            ref={severityBtnRef}
            data-testid="bulk-severity-button"
            aria-expanded={severityPopoverOpen}
            onClick={() => setSeverityPopoverOpen(v => !v)}
            disabled={!!loading}
            className={`${btnBase} bg-slate-600 hover:bg-slate-500`}
          >
            {loading === 'severity' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <ShieldAlert size={16} className="shrink-0" />}
            Set Severity
          </button>
        )}

        {/* Bulk Delete */}
        {onDelete && (
          <button
            data-testid="bulk-delete-button"
            onClick={handleDelete}
            disabled={!!loading}
            className={`${btnBase} bg-rose-600 hover:bg-rose-500`}
          >
            {loading === 'delete' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <Trash2 size={16} className="shrink-0" />}
            Delete {count}
          </button>
        )}

        {/* Export to CSV */}
        {onExportCSV && (
          <button
            data-testid="bulk-export-csv-button"
            onClick={onExportCSV}
            disabled={!!loading}
            className={`${btnBase} bg-emerald-600 hover:bg-emerald-500`}
          >
            <Download size={16} className="shrink-0" />
            Export to CSV
          </button>
        )}

        {/* Ungroup */}
        {hasGroupedSelected && onUngroup && (
          <button
            data-testid="bulk-ungroup-button"
            onClick={handleUngroup}
            disabled={!!loading}
            title="Remove from group"
            className={`${btnBase} bg-slate-600 hover:bg-slate-500`}
          >
            {loading === 'ungroup' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <Unlink size={16} className="shrink-0" />}
            Ungroup
          </button>
        )}

        {/* Group — trigger only; popover rendered below */}
        {onGroup && (
          <button
            ref={groupBtnRef}
            data-testid="bulk-group-button"
            aria-expanded={groupPopoverOpen}
            onClick={() => setGroupPopoverOpen(v => !v)}
            disabled={!!loading}
            className={`${btnBase} bg-blue-600 hover:bg-blue-500`}
          >
            {loading === 'group' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <Tag size={16} className="shrink-0" />}
            {hasGroupedSelected ? 'Change Group' : 'Group'}
          </button>
        )}

        <div className="w-px h-6 bg-slate-600 mx-1 shrink-0" />

        {/* Clear selection */}
        <button
          data-testid="bulk-clear-button"
          aria-label="Clear selection"
          onClick={onClear}
          title="Clear selection"
          className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-700 transition-colors text-slate-400 hover:text-slate-200 shrink-0"
        >
          <X size={18} />
        </button>
      </div>

      {/* ── Popovers ────────────────────────────────────────────────────────────
          Rendered as direct children of the outer `relative` wrapper so they
          escape the overflow-x:auto scroll container and never get clipped.
          `bottom-full mb-2` opens them upward; `left-1/2 -translate-x-1/2`
          centres them over the bar on mobile.                                */}

      {/* Move to Folder popover */}
      {onMoveToFolder && folderPopoverOpen && (
        <div
          ref={folderPopoverRef}
          data-testid="bulk-move-folder-popover"
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-3 w-56 z-[70]"
        >
          <p className="text-xs font-semibold text-slate-200 mb-2">Move {count} test{count !== 1 ? 's' : ''} to:</p>
          <select
            aria-label="Select destination folder"
            data-testid="bulk-move-folder-select"
            value={selectedFolderId}
            onChange={(e) => setSelectedFolderId(e.target.value)}
            className="w-full text-xs bg-slate-700 border border-slate-600 rounded-lg px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-100"
          >
            {folders.length === 0
              ? <option value="">No folders available</option>
              : folders.map((f) => (
                <option key={f._id} value={f._id}>
                  {f.path === '/root' ? 'Root' : f.name}
                </option>
              ))
            }
          </select>
          <button
            data-testid="bulk-move-folder-confirm"
            onClick={handleMoveToFolder}
            disabled={!selectedFolderId}
            className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
          >
            Move
          </button>
        </div>
      )}

      {/* Edit Tags popover */}
      {onEditTags && tagsPopoverOpen && (
        <div
          ref={tagsPopoverRef}
          data-testid="bulk-tags-popover"
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-3 w-60 z-[70]"
        >
          <p className="text-xs font-semibold text-slate-200 mb-2">Add tags to {count} test{count !== 1 ? 's' : ''}:</p>
          {pendingTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {pendingTags.map((t) => (
                <span key={t} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs bg-emerald-900 text-emerald-300">
                  {t}
                  <button
                    type="button"
                    aria-label={`Remove tag ${t}`}
                    onClick={() => setPendingTags((prev) => prev.filter((x) => x !== t))}
                    className="ml-0.5 hover:text-emerald-100"
                  >
                    <X size={9} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            ref={tagInputRef}
            autoFocus
            type="text"
            aria-label="Add tags"
            data-testid="bulk-tags-input"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addPendingTag(tagInput); }
              if (e.key === 'Backspace' && !tagInput && pendingTags.length > 0) {
                setPendingTags((prev) => prev.slice(0, -1));
              }
            }}
            placeholder="Type tag + Enter"
            className="w-full text-xs bg-slate-700 border border-slate-600 rounded-lg px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-100 placeholder:text-slate-400"
          />
          <button
            data-testid="bulk-apply-tags-button"
            onClick={handleApplyTags}
            disabled={pendingTags.length === 0}
            className="w-full py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
          >
            Apply Tags
          </button>
          <p className="text-[10px] text-slate-400 mt-1">Tags are added to existing tags on each case.</p>
        </div>
      )}

      {/* Set Severity popover */}
      {onEditSeverity && severityPopoverOpen && (
        <div
          ref={severityPopoverRef}
          data-testid="bulk-severity-popover"
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-3 w-40 z-[70]"
        >
          <p className="text-xs font-semibold text-slate-200 mb-2">Set severity:</p>
          {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).map((sev) => (
            <button
              key={sev}
              data-testid={`bulk-severity-option-${sev.toLowerCase()}`}
              onClick={() => handleEditSeverity(sev)}
              className="w-full text-left px-2 py-1.5 text-xs rounded-lg hover:bg-slate-700 text-slate-200 transition-colors"
            >
              {sev.charAt(0) + sev.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      )}

      {/* Group popover */}
      {onGroup && groupPopoverOpen && (
        <div
          ref={popoverRef}
          data-testid="bulk-group-popover"
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4 w-64 z-[70]"
        >
          <p className="text-xs font-semibold text-slate-200 mb-3">
            {hasGroupedSelected
              ? `Change group for ${count} execution${count !== 1 ? 's' : ''}`
              : `Assign group to ${count} execution${count !== 1 ? 's' : ''}`}
          </p>
          <form onSubmit={handleGroupSubmit} className="flex flex-col gap-2">
            <input
              ref={(el) => { (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el; el?.focus(); }}
              type="text"
              aria-label="Group name"
              data-testid="bulk-group-name-input"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. Nightly Sanity"
              className="w-full px-3 py-2 text-sm bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-100 placeholder:text-slate-400"
            />
            <button
              type="submit"
              data-testid="bulk-apply-group-button"
              disabled={!groupName.trim()}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply Group
            </button>
          </form>
        </div>
      )}
    </div>
    </div>
  );
}
